import React from 'react';

export function PageHeader({ title, subtitle, right, className = '' }) {
  return (
    <div className={`flex flex-wrap items-end justify-between gap-4 px-[22px] pt-[26px] pb-[14px] ${className}`}>
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="text-[13.5px] text-[#C7C4CB] mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Chips({ options, value, onChange, className = '', testId = 'chips' }) {
  return (
    <div className={`flex gap-2 overflow-x-auto no-scrollbar ${className}`} data-testid={testId}>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} className={`chip ${value === o ? 'on' : ''}`} data-testid={`chip-${o.toLowerCase().replace(/\s+/g, '-')}`}>{o}</button>
      ))}
    </div>
  );
}

export function StatCard({ label, value, trend, icon: Icon, accent = '#8B2CFF', testId }) {
  const up = trend >= 0;
  return (
    <div className="surface p-5 flex flex-col gap-3 min-w-0" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[12.5px] text-[#C7C4CB] font-medium">{label}</span>
        {Icon && <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}><Icon size={16} /></span>}
      </div>
      <div className="text-[26px] font-bold tracking-tight leading-none">{value}</div>
      {trend !== undefined && <div className={`text-[12px] font-medium ${up ? 'text-[#4ADE80]' : 'text-[#FF6B81]'}`}>{up ? '+' : ''}{trend}% vs last month</div>}
    </div>
  );
}

export function BarChart({ data, height = 140, color = '#8B2CFF', labels }) {
  const max = Math.max(...data);
  return (
    <div className="w-full">
      <div className="flex items-end gap-[6px]" style={{ height }}>
        {data.map((v, i) => (
          <div key={i} className="flex-1 rounded-t-[3px] relative group" style={{ height: `${(v / max) * 100}%`, background: `linear-gradient(180deg, ${color}, ${color}55)` }}>
            <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-[#FFFFFF] opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 px-1.5 py-0.5 rounded">{v}</span>
          </div>
        ))}
      </div>
      {labels && <div className="flex gap-[6px] mt-2">{labels.map((l, i) => <div key={i} className="flex-1 text-center text-[10px] text-[#8A8694]">{l}</div>)}</div>}
    </div>
  );
}

export function LineSpark({ data, color = '#E72BD4', height = 60 }) {
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - ((v - min) / (max - min || 1)) * 100}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts} vectorEffect="non-scaling-stroke" />
      <polygon fill={`${color}22`} points={`0,100 ${pts} 100,100`} />
    </svg>
  );
}

const STATUS_COLORS = {
  Published: '#4ADE80', Approved: '#38BDF8', 'Pending Review': '#FBBF24', Draft: '#B7BAC5', Rejected: '#FF6B81',
  Active: '#4ADE80', Suspended: '#FF6B81', Trial: '#FBBF24', Verified: '#38BDF8', Pending: '#FBBF24',
  Succeeded: '#4ADE80', Failed: '#FF6B81', Refunded: '#FBBF24', Paid: '#4ADE80', Scheduled: '#38BDF8', Live: '#FF2E4D',
};
export function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || '#B7BAC5';
  return <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2 py-[3px] rounded-full" style={{ background: `${c}1f`, color: c }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />{status}</span>;
}

export function Table({ columns, rows, rowKey = 'id', testId }) {
  return (
    <div className="surface overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#8A8694] border-b border-[#26212F]">
              {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[rowKey]} className="border-b border-white/5 hover:bg-white/[0.04] transition-colors">
                {columns.map((c) => <td key={c.key} className="px-4 py-3 align-middle whitespace-nowrap">{c.render ? c.render(r) : r[c.key]}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-[#8A8694]">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="surface flex flex-col items-center justify-center text-center py-16 px-6 mx-[22px]">
      {Icon && <Icon size={34} className="text-[#8A8694] mb-3" />}
      <h3 className="text-[16px] font-semibold">{title}</h3>
      {text && <p className="text-[13px] text-[#C7C4CB] mt-1 max-w-[380px]">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Grid({ children }) {
  return <div className="px-[22px] grid gap-[12px] grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">{children}</div>;
}
